import React, { FC, createContext, useState } from "react";
import Cookies from "js-cookie";
import config from "../utils/config";

import {
  AuthContextData,
  AuthProviderProps,
  UserData,
} from "../interface/useAuth";
import {
  ClearDataFunction,
  LoadDataFunction,
  RefetchDataFunction,
  SetCookieTokenFunction,
  SetDataFunction,
} from "../types/useAuth";

const AuthContext = createContext<AuthContextData | undefined>(undefined);

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be called within a AuthProvider");
  }
  return context;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [token, setToken] = useState<string>("");
  const [openNotification, setOpenNotification] = useState(false);
  const [isNotification, setIsNotification] = useState(false);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);

  const set_data: SetDataFunction = async (data: UserData) => {
    Cookies.set(config.key.admin, JSON.stringify(data));
    setUser(data);
  };

  const loadData: LoadDataFunction = async () => {
    const tokenCookie = Cookies.get(config.key.token);
    if (tokenCookie) {
      setToken(tokenCookie);
    }
    const userCookie = Cookies.get(config.key.admin);
    if (userCookie) {
      setUser(JSON.parse(userCookie));
    }
    const refreshTokenCookie = Cookies.get(config.key.refreshToken);
    if (refreshTokenCookie) {
      setRefreshToken(refreshTokenCookie);
    }
  };

  const refetchData: RefetchDataFunction = () => {};
  const clear_data: ClearDataFunction = () => {
    Cookies.remove(config.key.admin);
    Cookies.remove(config.key.token);
    Cookies.remove(config.key.refreshToken);
    setToken("");
    setUser(null);
    setRefreshToken(null);
  };

  const handleNotification = () => setOpenNotification(!openNotification);
  const handleIsNotification = (data: boolean) => setIsNotification(data);

  const set_cookie_token: SetCookieTokenFunction = async (data) => {
    const expiresInDays = data.accessToken.maxAge / 86400; // 1 day = 86400 seconds
    setToken(data.accessToken);
    Cookies.set(config.key.token, data?.accessToken, {
      path: "/",
      expires: expiresInDays,
    });
    setRefreshToken(data.refreshToken);
    Cookies.set(config.key.refreshToken, data?.refreshToken, {
      path: "/", // Set path to root
      expires: expiresInDays, // Set expiration in days,
    });
  };
  return (
    <AuthContext.Provider
      value={{
        set_data,
        token,
        refreshToken,
        set_cookie_token,
        loadData,
        openNotification,
        handleNotification,
        handleIsNotification,
        isNotification,
        user,
        refetchData,
        clear_data,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
